// Firestore Rules needed:
// match /cards/{cardId} {
//   allow read: if true;
//   allow create: if request.auth != null;
//   allow update, delete: if request.auth != null && request.auth.uid == resource.data.authorId;
// }

import React, { useState, useRef, useEffect } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from './firebase';

import { Routes, Route, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter } from "./Components/ui/card";
import { Input } from "./Components/ui/input";
import { Textarea } from "./Components/ui/textarea";
import { Button } from "./Components/ui/button";
import ColorSwatchPicker from './Components/ui/ColorSwatchPicker';
import TemplateColorSwatchPicker from './Components/ui/TemplateColorSwatchPicker';


import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./Components/ui/dialog";
import { Facebook, Twitter, Linkedin, Link2, Download, Share2, RefreshCw } from 'lucide-react';
import EmotionToggle from './Components/ui/EmotionToggle';
import { WallOfEmotions } from './Components/Wall/WallOfEmotions';
import { saveCard, updateCard } from "./services/cardService";
import EmotionModeToggle from './Components/ui/EmotionModeToggle'; // adjust path if different
import FooterNote from "./Components/ui/FooterNote";
import Privacy from "./Privacy"; // ✅ Use default import
import { useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import RichTextEditor from './Components/RichTextEditor';
import { useVisitorCardLimit } from './hook/useVisitorCardLimit';
import { Instagram } from 'lucide-react';
import CardViewer from './Components/Wall/CardViewer';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';
import { uploadFile } from './utils/fileUpload';











const gradients = {
  white: 'bg-white',
  sunset: 'bg-gradient-to-r from-orange-500 to-pink-500',
  ocean: 'bg-gradient-to-r from-blue-400 to-emerald-400',
  forest: 'bg-gradient-to-r from-green-400 to-teal-500',
  lavender: 'bg-gradient-to-r from-purple-400 to-pink-400',
  golden: 'bg-gradient-to-r from-yellow-400 to-orange-500',
  midnight: 'bg-gradient-to-r from-blue-900 to-purple-900',
  spring: 'bg-gradient-to-r from-green-300 to-yellow-300',
  autumn: 'bg-gradient-to-r from-red-500 to-yellow-500'
};

const gradientColors = {
  white: { start: '#ffffff', end: '#ffffff' },
  sunset: { start: '#f97316', end: '#ec4899' },
  ocean: { start: '#60a5fa', end: '#34d399' },
  forest: { start: '#4ade80', end: '#14b8a6' },
  lavender: { start: '#a78bfa', end: '#f472b6' },
  golden: { start: '#facc15', end: '#f97316' },
  midnight: { start: '#1e3a8a', end: '#581c87' },
  spring: { start: '#86efac', end: '#fde047' },
  autumn: { start: '#ef4444', end: '#facc15' }
};

const textColors = {
  white: ['#000000', '#333333', '#666666'],
  sunset: ['#FFFFFF', '#FFD700', '#000000'],
  ocean: ['#FFFFFF', '#00008B', '#000000'],
  forest: ['#FFFFFF', '#FFFF00', '#000000'],
  lavender: ['#FFFFFF', '#800080', '#000000'],
  golden: ['#FFFFFF', '#8B4513', '#000000'],
  midnight: ['#FFFFFF', '#00FFFF', '#FFD700'],
  spring: ['#000000', '#FF1493', '#008000'],
  autumn: ['#FFFFFF', '#8B4513', '#000000']
};

const emotions = {
  gratitude: 'Gratitude',
  happiness: 'Happiness',
  excitement: 'Excitement',
  inspiration: 'Inspiration',
  appreciation: 'Appreciation',
  encouragement: 'Encouragement',
  sympathy: 'Sympathy',
  congratulations: 'Congratulations'
};





const ShareDialog = ({ selectedEmotion, imageData }) => {
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  const cardUrl = "https://express.arake.app";
  const shareText = `I just created a heartfelt card of “${selectedEmotion || 'an emotion'}” on Thoughts! 🎨✨`;

  const fullShareText = `${shareText}\nDon’t just feel it — express it. Try it now at ${cardUrl}`;



  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Share2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share your message</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 pt-4">
          {cardUrl && (
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(cardUrl);
                alert('Copied link! You can paste it in your Instagram story or bio.');
              }}
            >
              <Instagram className="mr-2 h-4 w-4" /> Instagram
            </Button>
          )}

          <Button variant="outline" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(cardUrl)}`, '_blank')}>
            <Facebook className="mr-2 h-4 w-4" /> Facebook
          </Button>
          <Button variant="outline" onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(fullShareText)}`, '_blank')}>
            <Twitter className="mr-2 h-4 w-4" /> X
          </Button>
          <Button variant="outline" onClick={() => {
            navigator.clipboard.writeText(fullShareText);
            window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(cardUrl)}`, '_blank');
          }}>
            <Linkedin className="mr-2 h-4 w-4" /> LinkedIn
          </Button>
          <Button variant="outline" onClick={() => {
            navigator.clipboard.writeText(cardUrl);  // Make sure cardUrl is defined above
            alert('Card link copied to clipboard!');
          }}>
            <Link2 className="mr-2 h-4 w-4" /> Copy Link
          </Button>

        </div>
      </DialogContent>
    </Dialog>
  );
};

const Express = ({ mode, setMode, onPublish, editingCard, user }) => {
  const [recipient, setRecipient] = useState('Type Recipient');
  const [sender, setSender] = useState('Type Your Name');

  const [selectedEmotion, setSelectedEmotion] = useState('Type a moment (e.g., Gratitude, Joy, Support)');
  const [selectedGradient, setSelectedGradient] = useState(editingCard?.gradient || 'white');
  const [textColor, setTextColor] = useState(editingCard?.textColor || textColors.white[0]);
  const [customStartColor, setCustomStartColor] = useState(editingCard?.customStartColor || '#f97316');
  const [customEndColor, setCustomEndColor] = useState(editingCard?.customEndColor || '#ec4899');
  const [greetingLabel, setGreetingLabel] = useState('Dear');
  const [closingLabel, setClosingLabel] = useState('Yours');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const recipientRef = useRef(null);
  const senderRef = useRef(null);
  const [message, setMessage] = useState(editingCard?.message || '');
  const [linkUrl, setLinkUrl] = useState(editingCard?.url || '');
  const [attachments, setAttachments] = useState(editingCard?.attachments || []);

  const isInitialMount = useRef(!!editingCard);

  const emotionRef = useRef(null);
  const [isEmotionFocused, setIsEmotionFocused] = useState(false);
  const { visitorId, limitReached } = useVisitorCardLimit();
  const navigate = useNavigate();
  const closingLabelRef = useRef(null);






  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (selectedGradient !== 'custom') {
      setTextColor(textColors[selectedGradient][0]);
    }
  }, [selectedGradient]);

  useEffect(() => {
    if (recipientRef.current && recipientRef.current.innerText.trim() !== "") {
      recipientRef.current.style.color = textColor;
    }
    if (emotionRef.current && emotionRef.current.innerText.trim() !== "") {
      emotionRef.current.style.color = textColor;
    }
    if (senderRef.current && senderRef.current.innerText.trim() !== "") {
      senderRef.current.style.color = textColor;
    }
    if (closingLabelRef.current && closingLabelRef.current.innerText.trim() !== "") {
      closingLabelRef.current.style.color = textColor;
    }
  }, [textColor]);





  const handleDownload = async () => {
    const cardElement = document.getElementById('card-preview');
    if (!cardElement) return;

    // Temporarily hide floating buttons
    document.querySelectorAll('.hide-on-download').forEach(el => el.setAttribute('data-download', 'true'));

    // ⬇️ Use null background to preserve transparency
    const canvas = await html2canvas(cardElement, {
      backgroundColor: getComputedStyle(cardElement).backgroundColor,
      scale: 2,
      useCORS: true
    });

    // Restore buttons
    document.querySelectorAll('.hide-on-download').forEach(el => el.removeAttribute('data-download'));

    // Trigger download
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = 'Express_Message.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePublish = async () => {
    if (!user) {
      alert("Please sign in with Google to publish.");
      return;
    }
    const plainText = message?.replace(/<[^>]*>/g, '').trim();
    if (!plainText) {
      alert("Please write a message before publishing.");
      return;
    }

    const newCardId = crypto.randomUUID(); // generate an ID for this card

    // ✅ Generate imageData (base64 PNG) using canvas logic
    const imageData = generateImageData({
      mode,
      message,
      sender,
      recipient,
      emotion: selectedEmotion,
      gradient: selectedGradient,
      textColor,
      customStartColor,
      customEndColor
    });

    // ✅ Optional: upload to Firebase Storage (if using)
    /*
    const storageRef = ref(storage, `cards/${newCardId}.png`);
    await uploadString(storageRef, imageData, 'data_url');
    const imageUrl = await getDownloadURL(storageRef);
    */

    // ✅ Build the card object
    const newCard = {
      visitorId,
      mode,
      message,
      gradient: selectedGradient,
      textColor,
      appreciateCount: 0,
      imageData,
      authorId: user.uid,
      authorName: user.displayName || 'Anonymous',
      authorPhoto: user.photoURL || null,
      ...(selectedGradient === "custom" && {
        customStartColor,
        customEndColor
      }),
      ...(mode === "Classic" && {
        sender,
        recipient,
        emotion: selectedEmotion,
      }),
      ...(linkUrl.trim() && { url: linkUrl.trim() }),
      ...(attachments.length > 0 && { attachments }),
    };

    if (editingCard) {
      const success = await updateCard(editingCard.id, newCard);
      if (success) {
        alert("Your card has been updated!");
        handleReset();
        if (onPublish) onPublish();
      } else {
        alert("Something went wrong. Please try again.");
      }
    } else {
      const savedId = await saveCard(newCard, newCardId);
      if (savedId) {
        alert("Your message has been published to the Wall!");
        handleReset();
        if (onPublish) {
          onPublish();
        } else {
          navigate("/wall");
        }
      } else {
        alert("Something went wrong. Please try again.");
      }
    }
  };

  const generateImageData = ({
    mode,
    message,
    sender,
    recipient,
    emotion,
    gradient,
    textColor,
    customStartColor,
    customEndColor
  }) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 600;
      canvas.height = 400;

      const gradientColorsMap = {
        sunset: { start: '#f97316', end: '#ec4899' },
        ocean: { start: '#60a5fa', end: '#34d399' },
        forest: { start: '#4ade80', end: '#14b8a6' },
        lavender: { start: '#a78bfa', end: '#f472b6' },
        golden: { start: '#facc15', end: '#f97316' },
        midnight: { start: '#1e3a8a', end: '#581c87' },
        spring: { start: '#86efac', end: '#fde047' },
        autumn: { start: '#ef4444', end: '#facc15' }
      };

      const colors = gradient === 'custom'
        ? { start: customStartColor, end: customEndColor }
        : gradientColorsMap[gradient] || gradientColorsMap['sunset'];

      const gradientFill = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradientFill.addColorStop(0, colors.start);
      gradientFill.addColorStop(1, colors.end);

      ctx.fillStyle = gradientFill;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.globalCompositeOperation = 'destination-in';
      ctx.beginPath();
      ctx.roundRect(0, 0, canvas.width, canvas.height, 10);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';

      ctx.fillStyle = textColor;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      let yPosition = 60;

      // 🔐 Quick mode override: blank out classic fields
      if (mode === 'Quick') {
        sender = '';
        recipient = '';
        emotion = '';
      }

      // 🎨 Classic content
      if (mode === 'Classic') {
        ctx.font = 'bold 28px Arial, sans-serif';
        ctx.fillText(`${greetingLabel} ${recipient || 'Recipient'}`, 40, yPosition);


        yPosition += 50;
      }

      if (mode === 'Classic' && emotion) {
        ctx.font = 'italic 22px Arial, sans-serif';
        ctx.fillText(`Expressing ${emotion}:`, 40, yPosition);
        yPosition += 35;
      }

      // 📝 Message
      ctx.font = '20px Arial, sans-serif';
      const messageText = (message || 'Your message will appear here...').replace(/<[^>]*>/g, '');
      const words = messageText.split(' ');
      let line = '';
      const maxWidth = canvas.width - 80;
      const lineHeight = 25;

      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && n > 0) {
          ctx.fillText(line.trim(), 40, yPosition);
          line = words[n] + ' ';
          yPosition += lineHeight;
        } else {
          line = testLine;
        }
      }

      if (line.trim()) {
        ctx.fillText(line.trim(), 40, yPosition);
        yPosition += lineHeight;
      }

      yPosition += 30;
      ctx.textAlign = 'right';

      if (mode === 'Classic') {
        ctx.font = 'bold 22px Arial, sans-serif';

        ctx.fillText(`${closingLabel},`, canvas.width - 40, yPosition);

        yPosition += 30;

        ctx.font = 'bold 22px Arial, sans-serif';

        ctx.fillText(sender || 'Your Name', canvas.width - 40, yPosition);
      }

      return canvas.toDataURL('image/png', 1.0);
    } catch (error) {
      console.error('Image generation failed:', error);
      return null;
    }
  };



  const handleFileAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} exceeds 10MB limit`);
        continue;
      }
      try {
        const result = await uploadFile(file, 'cards/attachments');
        setAttachments(prev => [...prev, result]);
      } catch (err) {
        console.error('File upload failed:', err);
        alert(`Failed to upload ${file.name}`);
      }
    }
    e.target.value = '';
  };

  const handleReset = () => {
    setGreetingLabel('Dear');
    setRecipient('Type Recipient');
    setSelectedEmotion('Type a moment (e.g., Gratitude, Joy, Support)');
    setMessage('');
    setClosingLabel('Yours');
    setSender('Type Your Name');
    setLinkUrl('');
    setAttachments([]);
  };

  return (
    <div className="max-w-7xl mx-auto mt-4 px-2 md:px-4">




      <div className="w-full max-w-xl mx-auto">


        <div
          id="card-preview"
          className="relative p-4 md:p-6 rounded-lg border shadow-md min-h-[280px] md:min-h-[320px]"
          style={{
            background: selectedGradient === 'custom'
              ? `linear-gradient(to right, ${customStartColor}, ${customEndColor})`
              : `linear-gradient(to right, ${gradientColors[selectedGradient]?.start || '#fff'}, ${gradientColors[selectedGradient]?.end || '#fff'})`,
            color: textColor
          }}
        >
          {mode === 'Classic' && (
            <div className="flex flex-wrap items-baseline gap-1 mb-2">
              <div
                contentEditable
                suppressContentEditableWarning
                onFocus={(e) => {
                  if (e.currentTarget.innerText.trim() === "Dear") {
                    e.currentTarget.innerText = "Dear";
                  }
                  e.currentTarget.style.color = textColor;
                  e.currentTarget.classList.add(
                    "outline", "outline-white", "bg-white/10",
                    "px-2", "py-1", "rounded", "min-w-[60px]", "inline-block", "whitespace-nowrap"
                  );
                }}
                onBlur={(e) => {
                  const val = e.currentTarget.textContent.trim();
                  setGreetingLabel(val || "Dear");
                  if (!val) {
                    e.currentTarget.innerText = "Dear";
                    e.currentTarget.style.setProperty("color", textColor, "important");
                  }
                  e.currentTarget.classList.remove("outline", "outline-white", "bg-white/10", "px-2", "py-1", "rounded", "min-w-[60px]", "inline-block", "whitespace-nowrap");
                }}
                className="font-bold"
              >
                {greetingLabel || "Dear"}
              </div>

              <div
                ref={recipientRef}
                contentEditable
                suppressContentEditableWarning
                onFocus={(e) => {
                  if (e.currentTarget.innerText.trim() === "Type Recipient") {
                    e.currentTarget.innerText = " ";
                  }
                  e.currentTarget.style.color = textColor;
                  e.currentTarget.style.caretColor = textColor;
                  e.currentTarget.classList.add(
                    "outline", "outline-white", "bg-white/10", "px-2", "py-1", "rounded", "min-w-[200px]", "inline-block", "whitespace-nowrap"
                  );
                }}
                onBlur={(e) => {
                  const val = e.currentTarget.innerText.trim();
                  setRecipient(val);

                  if (!val || val === "Type Recipient") {
                    recipientRef.current.innerText = "Type Recipient";
                    // explicitly apply placeholder color again
                    recipientRef.current.style.setProperty("color", textColor, "important");
                  } else {
                    // reset color to default (textColor)
                    recipientRef.current.style.color = textColor;
                  }

                  e.currentTarget.classList.remove("outline", "bg-white/10", "px-2", "py-1");
                }}

                className={`whitespace-pre-line ${!recipient || recipient === "Type Recipient"
                  ? "text-white/70 italic font-normal"
                  : "text-white font-bold"}`}>


                {recipient || "Type Recipient"}
              </div>

            </div>
          )}

          {mode === 'Classic' && (
            <div className="mb-2">
              <span className="font-bold">Expressing</span>{" "}
              <span
                ref={emotionRef}
                contentEditable
                suppressContentEditableWarning
                onFocus={(e) => {
                  setIsEmotionFocused(true);
                  if (e.currentTarget.innerText.trim() === "Type a moment (e.g., Gratitude, Joy, Support)") {
                    e.currentTarget.innerText = "";
                  }
                  e.currentTarget.style.color = textColor;
                  e.currentTarget.style.caretColor = textColor;
                  e.currentTarget.classList.add("outline", "outline-white", "bg-white/10", "px-2", "py-1", "rounded", "min-w-[200px]", "inline-block", "whitespace-nowrap");
                }}
                onBlur={(e) => {
                  setIsEmotionFocused(false);
                  const val = e.currentTarget.innerText.trim();
                  setSelectedEmotion(val);
                  if (!val) {
                    emotionRef.current.innerText = "Type a moment (e.g., Gratitude, Joy, Support)";
                    emotionRef.current.style.setProperty("color", textColor, "important");
                  }
                  e.currentTarget.classList.remove("outline", "bg-white/10", "px-2", "py-1");
                }}

                className={`whitespace-pre-line ${!selectedEmotion || selectedEmotion === "Type a moment (e.g., Gratitude, Joy, Support)"
                  ? "text-white/70 italic font-normal"
                  : "text-white font-bold"}`}>
                {selectedEmotion || "Type a moment (e.g., Gratitude, Joy, Support)"}
              </span>

            </div>
          )}

          <div className="mb-8">
            <RichTextEditor
              content={message}
              onUpdate={(html) => setMessage(html)}
              textColor={textColor}
              placeholder="Type your message here..."
            />
          </div>

          {mode === 'Classic' && (
            <div className="text-right flex flex-col items-end space-y-1 mt-4">
              {/* Editable 'Yours' label */}
              <div
                ref={closingLabelRef}
                contentEditable
                suppressContentEditableWarning
                onFocus={(e) => {
                  if (e.currentTarget.innerText.trim() === "Yours") {
                    e.currentTarget.innerText = "Yours";
                  }
                  e.currentTarget.style.color = textColor;
                  e.currentTarget.classList.add(
                    "outline", "outline-white", "bg-white/10",
                    "px-2", "py-1", "rounded", "min-w-[60px]", "inline-block", "whitespace-nowrap"
                  );
                }}
                onBlur={(e) => {
                  const val = e.currentTarget.textContent.trim();
                  setClosingLabel(val || "Yours");
                  if (!val) {
                    e.currentTarget.innerText = "Yours";
                    e.currentTarget.style.setProperty("color", textColor, "important");
                  }
                  e.currentTarget.classList.remove(
                    "outline", "bg-white/10", "px-2", "py-1", "rounded", "min-w-[60px]", "inline-block", "whitespace-nowrap"
                  );
                }}
                className="text-white font-bold"
              >
                {closingLabel || "Yours"}
              </div>

              {/* Editable sender name */}
              <div
                ref={senderRef}
                contentEditable
                suppressContentEditableWarning
                onFocus={(e) => {
                  if (e.currentTarget.innerText.trim() === "Type Your Name") {
                    e.currentTarget.innerText = "";
                  }
                  e.currentTarget.style.color = textColor;
                  e.currentTarget.classList.add(
                    "outline", "outline-white", "bg-white/10",
                    "px-2", "py-1", "rounded", "min-w-[130px]", "inline-block", "whitespace-nowrap"
                  );
                  e.currentTarget.style.caretColor = textColor;
                }}
                onBlur={(e) => {
                  const val = e.currentTarget.innerText.trim();
                  setSender(val);
                  if (!val) {
                    senderRef.current.innerText = "Type Your Name";
                    senderRef.current.style.setProperty("color", textColor, "important");
                  }
                  e.currentTarget.classList.remove("outline", "bg-white/10", "px-2", "py-1");
                }}
                className={`whitespace-pre-line ${!sender || sender === "Type Your Name"
                  ? "text-white/70 italic font-normal"
                  : "text-white font-bold"}`}
              >
                {sender || "Type Your Name"}
              </div>
            </div>
          )}




          {/* 🎨 Floating Card Toolbar */}
          <div className="absolute bottom-3 left-3 flex gap-5 hide-on-download">
            <button
              onClick={() => setShowTemplatePicker(!showTemplatePicker)}
              className="text-lg hover:scale-110 transition"
              title="Choose Background"
            >
              🎨
            </button>
            <button
              onClick={() => setShowTextColorPicker(!showTextColorPicker)}
              className="text-lg ml-2 underline hover:scale-110 transition"
              title="Text Color"
            >
              A
            </button>

          </div>

        </div>
        {/* Controls that appear below card after clicking */}
        {showTemplatePicker && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium">Choose a Template</label>
              <select
                value={selectedGradient}
                onChange={(e) => setSelectedGradient(e.target.value)}
                className="w-full p-2 border rounded"
              >
                {Object.keys(gradients).map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
                <option value="custom">🎨 Custom</option>
              </select>
            </div>

            {selectedGradient === "custom" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 text-sm font-medium">Start Color</label>
                  <TemplateColorSwatchPicker selectedColor={customStartColor} onSelect={setCustomStartColor} />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium">End Color</label>
                  <TemplateColorSwatchPicker selectedColor={customEndColor} onSelect={setCustomEndColor} />
                </div>
              </div>
            )}
          </div>
        )}


        {showTextColorPicker && (
          <div className="mt-4">
            <label className="block mb-1 text-sm font-medium">Text Color</label>
            <ColorSwatchPicker selectedColor={textColor} onSelect={setTextColor} />
          </div>
        )}


        <input
          type="url"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="Paste a YouTube, blog, or website link..."
          className="w-full mt-4 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        />

        {/* File Attachments */}
        <div className="mt-2">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-500 hover:text-gray-700">
            <span>📎</span>
            <span>Attach files (PDF, Word)</span>
            <input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              multiple
              className="hidden"
              onChange={handleFileAttach}
            />
          </label>
          {attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              {attachments.map((att, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-gray-100 rounded px-2 py-1">
                  <span>{att.type?.includes('pdf') ? '📄' : '📝'}</span>
                  <span className="truncate flex-1">{att.name}</span>
                  <span className="text-gray-400 text-xs">{(att.size / 1024).toFixed(0)} KB</span>
                  <button onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mt-4">
          <div className="flex space-x-2">
            <ShareDialog shareLabel={selectedEmotion} shareUrl="https://express.arake.app" />
            <Button variant="outline" size="icon" onClick={handleDownload}>
              <Download className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleReset}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          <Button variant="default" onClick={handlePublish}>
            {editingCard ? 'Update' : 'Publish to Wall'}
          </Button>
        </div>

      </div>
    </div>


  );
};

const App = () => {
  const mode = 'Quick';
  const setMode = () => {};
  const location = useLocation();
  const [showExpressModal, setShowExpressModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingCard, setEditingCard] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user') {
        alert('Sign-in failed: ' + error.message);
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      alert('Sign-out failed: ' + error.message);
    }
  };

  const showFooter = location.pathname === '/' || location.pathname === '/wall';

  useEffect(() => {
    document.body.style.overflow = showExpressModal ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [showExpressModal]);

  const handlePublishFromModal = () => {
    setShowExpressModal(false);
    setEditingCard(null);
    setRefreshKey(prev => prev + 1);
  };

  const handleEdit = (card) => {
    setEditingCard(card);
    setShowExpressModal(true);
  };

  const handleCloseModal = () => {
    setShowExpressModal(false);
    setEditingCard(null);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<WallOfEmotions key={refreshKey} onPost={() => { setEditingCard(null); setShowExpressModal(true); }} onEdit={handleEdit} user={user} onSignIn={handleSignIn} onSignOut={handleSignOut} />} />
          <Route path="/wall" element={<WallOfEmotions key={refreshKey} onPost={() => { setEditingCard(null); setShowExpressModal(true); }} onEdit={handleEdit} user={user} onSignIn={handleSignIn} onSignOut={handleSignOut} />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/card/:id" element={<CardViewer />} />
        </Routes>
      </main>

      {showExpressModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCloseModal}
          />
          <div className="relative bg-white rounded-t-2xl md:rounded-lg shadow-lg w-full md:max-w-xl mx-0 md:mx-4 max-h-[95vh] md:max-h-[90vh] overflow-y-auto z-50 pb-6">
            <button
              onClick={handleCloseModal}
              className="absolute top-3 right-3 z-10 text-gray-400 hover:text-gray-600 bg-white/80 rounded-full p-2 md:p-1"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <Express key={editingCard?.id || 'new'} mode={mode} setMode={setMode} onPublish={handlePublishFromModal} editingCard={editingCard} user={user} />
          </div>
        </div>
      )}

      {showFooter && <FooterNote />}
    </div>
  );
};


export default App;

